import { collection, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import * as roadmapService from './roadmapService';
import { getRoadmapTrackIdFromUserDeckData, userCanAccessRoadmapTrack } from '../utils/roadmapSubscription';
import {
  cardMatchesProgressRow,
  resolveProgressForDeckCard
} from '../utils/cookbookCardProgress';
import {
  CARD_PHASE,
  isCardInLearningPhase,
  isCardMastered,
  resolveCardPhase,
  resolveSrsNextReviewDate
} from '../components/SRS/algorithm';

/** Short-lived in-session cache so header due badges do not re-scan Firestore on every render. */
const REVIEW_SNAPSHOT_TTL_MS = 30_000;
const reviewSnapshotCache = new Map();
/** Cached roadmap per-card rows (expensive: one cards subcollection read per roadmap deck). */
const roadmapTrackRowsCache = new Map();

function isReviewSnapshotFresh(entry) {
  return entry && Date.now() - entry.at < REVIEW_SNAPSHOT_TTL_MS;
}

/**
 * Decks + SRS progress rows for a user (shared by due-count, Home, and Review tab).
 * @param {string} userId
 * @param {*} srs
 */
export async function fetchUserReviewSnapshot(userId, srs) {
  const cacheKey = userId;
  const cached = reviewSnapshotCache.get(cacheKey);
  if (isReviewSnapshotFresh(cached)) {
    return cached.value;
  }

  const value = await Promise.all([
    getDocs(collection(db, `users/${userId}/decks`)),
    srs.fetchAllCardProgressRows()
  ]);

  reviewSnapshotCache.set(cacheKey, { at: Date.now(), value });
  roadmapTrackRowsCache.delete(cacheKey);
  return value;
}

/** Call after study writes so due badges refresh on next read. */
export function invalidateUserReviewSnapshot(userId) {
  if (userId) {
    reviewSnapshotCache.delete(userId);
    roadmapTrackRowsCache.delete(userId);
  }
}

/**
 * End of the local calendar day for `now`.
 * @param {Date} [now]
 */
export function endOfLocalDay(now = new Date()) {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Start of the local calendar day for `now`.
 * @param {Date} [now]
 */
export function startOfLocalDay(now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Whether an SRS row belongs on the Review page for today.
 * Includes overdue cards and learning steps due later today (e.g. &lt;10m).
 *
 * @param {{ nextReview?: string|null }} row
 * @param {Date} [now]
 */
export function isSrsProgressRowDue(row, now = new Date()) {
  if (!row?.nextReview) return true;

  const phase = resolveCardPhase(row);
  const effectiveDue = resolveSrsNextReviewDate(row, now);
  if (!effectiveDue || Number.isNaN(effectiveDue.getTime())) return false;

  if (phase === CARD_PHASE.REVIEW) {
    const intervalDays = Number(row.interval);
    if (Number.isFinite(intervalDays) && intervalDays >= 1) {
      const dueDay = startOfLocalDay(effectiveDue);
      const today = startOfLocalDay(now);
      return dueDay.getTime() <= today.getTime();
    }
  }

  if (isCardInLearningPhase(row)) {
    if (effectiveDue <= now) return true;
    return effectiveDue <= endOfLocalDay(now);
  }

  if (effectiveDue <= now) return true;
  return effectiveDue <= endOfLocalDay(now);
}

/**
 * Library cookbook due card count (non-roadmap decks only).
 * @param {Array<{ id: string, dueCards?: number }>} dueDecksAll
 * @param {Map<string, boolean>} deckIsRoadmap
 */
export function computeLibraryDueCardCount(dueDecksAll, deckIsRoadmap) {
  return dueDecksAll
    .filter((deck) => !deckIsRoadmap.get(deck.id))
    .reduce((sum, deck) => sum + (deck.dueCards || 0), 0);
}

/**
 * Due track snippet count (roadmap cookbooks), respecting premium access.
 * @param {Array<{ roadmapTrackId?: string, nextReview?: string|null }>} trackRows
 * @param {boolean} isPremiumUser
 * @param {Date} [now]
 */
export function computeTrackDueCardCount(trackRows, isPremiumUser, now = new Date()) {
  return trackRows
    .filter((row) => userCanAccessRoadmapTrack(isPremiumUser, row.roadmapTrackId))
    .filter((row) => isSrsProgressRowDue(row, now))
    .length;
}

/**
 * User-facing tags for a track cookbook row on Review (language, topic, track).
 *
 * @param {{ language?: string, roadmapNodeTitle?: string, deckTags?: string[] }} deck
 */
export function formatTrackCookbookDisplayTags(deck) {
  const tags = [];
  const lang = String(deck?.language ?? '').trim();
  if (lang) {
    tags.push(lang.charAt(0).toUpperCase() + lang.slice(1));
  }
  if (deck?.roadmapNodeTitle) {
    tags.push(deck.roadmapNodeTitle);
  }
  for (const tag of deck?.deckTags || []) {
    if (typeof tag !== 'string') continue;
    const lower = tag.toLowerCase();
    if (lower === 'roadmap' || lower === 'cookbook') continue;
    if (!tags.includes(tag)) tags.push(tag);
  }
  return tags.slice(0, 3);
}

/**
 * Group due track snippet rows by their parent cookbook (one row per track deck).
 *
 * @param {Array<{ deckId?: string, deckName?: string, language?: string, roadmapTrackId?: string, roadmapNodeTitle?: string, deckTags?: string[], nextReview?: string|null }>} trackRows
 * @param {boolean} isPremiumUser
 * @param {Date} [now]
 */
export function buildTrackDueDecksFromSnippetRows(trackRows, isPremiumUser, now = new Date()) {
  const byDeckId = new Map();

  for (const row of trackRows || []) {
    if (!row?.deckId) continue;
    if (!userCanAccessRoadmapTrack(isPremiumUser, row.roadmapTrackId)) continue;
    if (!isSrsProgressRowDue(row, now)) continue;

    let deck = byDeckId.get(row.deckId);
    if (!deck) {
      deck = {
        id: row.deckId,
        name: row.deckName || 'Cookbook',
        language: row.language || '',
        roadmapTrackId: row.roadmapTrackId,
        roadmapNodeTitle: row.roadmapNodeTitle || '',
        deckTags: Array.isArray(row.deckTags) ? row.deckTags : [],
        dueCards: 0,
        nextReview: row.nextReview || null,
        tags: formatTrackCookbookDisplayTags(row)
      };
      byDeckId.set(row.deckId, deck);
    }

    deck.dueCards += 1;
    if (row.nextReview) {
      const rowDue = new Date(row.nextReview);
      const deckDue = deck.nextReview ? new Date(deck.nextReview) : null;
      if (!deckDue || rowDue < deckDue) {
        deck.nextReview = row.nextReview;
      }
    }
  }

  return Array.from(byDeckId.values()).sort((a, b) => {
    const aDue = a.nextReview ? new Date(a.nextReview).getTime() : 0;
    const bDue = b.nextReview ? new Date(b.nextReview).getTime() : 0;
    if (aDue !== bDue) return aDue - bDue;
    return (a.name || '').localeCompare(b.name || '');
  });
}

/**
 * Roadmap track snippet rows from progress + deck metadata only (no card subcollection reads).
 * Matches {@link buildRoadmapTrackSnippetRows} for due-count / badge rules because that
 * function only emits cards that already have progress.
 *
 * @param {*} decksSnapshot - Firestore QuerySnapshot for users/{uid}/decks
 * @param {Array<Record<string, unknown>>} progressRows
 * @param {*} srs
 */
export function buildRoadmapTrackSnippetRowsFromProgress(decksSnapshot, progressRows, srs) {
  const roadmapDeckById = new Map();
  for (const deckDoc of decksSnapshot.docs) {
    const deckData = deckDoc.data();
    const isCookbook = deckData.type === 'cookbook' || !deckData.type;
    if (!isCookbook) continue;
    if (!roadmapService.isRoadmapUserCookbook({ id: deckDoc.id, ...deckData })) continue;
    roadmapDeckById.set(deckDoc.id, { id: deckDoc.id, ...deckData });
  }

  const rows = [];
  for (const p of progressRows || []) {
    const deckId = p.deckId;
    const cardId = p.cardId;
    if (!deckId || !cardId) continue;

    const deckData = roadmapDeckById.get(deckId);
    if (!deckData) continue;

    const totalReviews = Number(p.totalReviews) || 0;
    const successfulReviews = Number(p.successfulReviews) || 0;
    const successRate =
      totalReviews > 0 ? Math.round((successfulReviews / totalReviews) * 100) : 0;
    const mastered = isCardMastered(p);
    const masteryVal = p.mastery ?? 0;

    rows.push({
      deckId,
      deckName: deckData.name || 'Cookbook',
      language: deckData.language || '',
      roadmapTrackId: getRoadmapTrackIdFromUserDeckData(deckData),
      roadmapNodeTitle: deckData.roadmapNodeTitle || '',
      deckTags: Array.isArray(deckData.tags) ? deckData.tags : [],
      cardId,
      cardTitle: 'Snippet',
      successRate,
      mastered,
      nextReview: p.nextReview || null,
      interval: p.interval,
      intervalMinutes: p.intervalMinutes ?? null,
      cardPhase: p.cardPhase,
      learningStep: p.learningStep,
      repetitions: p.repetitions,
      lastReviewed: p.lastReviewed || null,
      totalReviews,
      mastery: masteryVal,
      masteryLevel: srs.getMasteryLevel(masteryVal)
    });
  }

  rows.sort((a, b) => {
    if (a.mastered !== b.mastered) return a.mastered ? 1 : -1;
    if (a.successRate !== b.successRate) return a.successRate - b.successRate;
    return (a.deckName || '').localeCompare(b.deckName || '');
  });
  return rows;
}

/**
 * Re-fetch only the progress collection; reuse cached decks snapshot when fresh.
 */
export async function refreshUserReviewProgressRows(userId, srs) {
  const cached = reviewSnapshotCache.get(userId);
  if (isReviewSnapshotFresh(cached)) {
    const decksSnap = cached.value[0];
    const progressRows = await srs.fetchAllCardProgressRows();
    const value = [decksSnap, progressRows];
    reviewSnapshotCache.set(userId, { at: Date.now(), value });
    roadmapTrackRowsCache.delete(userId);
    return value;
  }
  return fetchUserReviewSnapshot(userId, srs);
}

/**
 * Cached wrapper — reuses rows when decks/progress snapshot is still fresh.
 * Loads every card in every roadmap deck (Review tab / guest track titles).
 */
export async function fetchRoadmapTrackSnippetRows(userId, decksSnapshot, progressRows, srs) {
  const cached = roadmapTrackRowsCache.get(userId);
  if (isReviewSnapshotFresh(cached)) {
    return cached.value;
  }
  const rows = await buildRoadmapTrackSnippetRows(userId, decksSnapshot, progressRows, srs);
  roadmapTrackRowsCache.set(userId, { at: Date.now(), value: rows });
  return rows;
}

/**
 * Non-roadmap cookbooks with due cards that still exist and have a solution (studyable).
 *
 * @param {string} userId
 * @param {Array<{ id: string }>} decks
 * @param {Array<Record<string, unknown>>} progressRows
 * @param {*} srs
 * @param {Map<string, boolean>} deckIsRoadmap
 */
export async function buildStudyableLibraryDueDecks(
  userId,
  decks,
  progressRows,
  srs,
  deckIsRoadmap
) {
  const dueDecksAll = srs.buildDueDecksFromProgress(decks, progressRows);
  const libraryDecks = dueDecksAll.filter((deck) => !deckIsRoadmap.get(deck.id));
  if (!userId || libraryDecks.length === 0) return [];

  const validated = await Promise.all(
    libraryDecks.map(async (deck) => {
      const studyableDueCount = await countStudyableDueCardsInDeck(
        userId,
        deck.id,
        progressRows,
        srs
      );
      if (studyableDueCount <= 0) return null;
      return { ...deck, dueCards: studyableDueCount };
    })
  );

  return validated.filter(Boolean);
}

/**
 * @param {string} userId
 * @param {string} deckId
 * @param {Array<Record<string, unknown>>} progressRows
 * @param {*} srs
 * @param {Date} [now]
 */
export async function countStudyableDueCardsInDeck(userId, deckId, progressRows, srs, now = new Date()) {
  if (!userId || !deckId) return 0;

  const cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${deckId}/cards`));
  const studyableCards = cardsSnap.docs
    .map((cardDoc) => ({ id: cardDoc.id, ...cardDoc.data() }))
    .filter((card) => card.solution && String(card.solution).trim());

  if (studyableCards.length === 0) return 0;

  const deckProgress = (progressRows || []).filter((row) => row.deckId === deckId);
  const dueProgress = srs.filterDueCardProgressList(deckProgress, now);
  return dueProgress.filter((row) =>
    studyableCards.some((card) => cardMatchesProgressRow(card, row))
  ).length;
}

/**
 * Delete SRS progress rows that are due but no longer map to a studyable cookbook card.
 * @returns {Promise<number>} count removed
 */
export async function pruneOrphanCardProgress(userId, progressRows, srs, now = new Date()) {
  if (!userId || !Array.isArray(progressRows) || progressRows.length === 0) return 0;

  const dueRows = progressRows.filter((row) => isSrsProgressRowDue(row, now));
  if (dueRows.length === 0) return 0;

  const deckIds = [...new Set(dueRows.map((row) => row.deckId).filter(Boolean))];
  /** @type {Map<string, Array<{ id: string, roadmapSnippetId?: string, solution?: string }>>} */
  const studyableCardsByDeck = new Map();

  await Promise.all(
    deckIds.map(async (deckId) => {
      const cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${deckId}/cards`));
      const cards = cardsSnap.docs
        .map((cardDoc) => ({ id: cardDoc.id, ...cardDoc.data() }))
        .filter((card) => card.solution && String(card.solution).trim());
      studyableCardsByDeck.set(deckId, cards);
    })
  );

  let pruned = 0;
  for (const row of dueRows) {
    const cards = studyableCardsByDeck.get(row.deckId) || [];
    if (cards.some((card) => cardMatchesProgressRow(card, row))) continue;

    const progressDocId =
      typeof row.id === 'string' && row.id.includes('_')
        ? row.id
        : `${row.deckId}_${row.cardId}`;
    if (!progressDocId || !row.deckId || !row.cardId) continue;

    try {
      await deleteDoc(doc(db, `users/${userId}/progress`, progressDocId));
      pruned += 1;
    } catch (error) {
      console.warn('pruneOrphanCardProgress failed', progressDocId, error);
    }
  }

  if (pruned > 0) {
    invalidateUserReviewSnapshot(userId);
  }

  return pruned;
}

export async function buildRoadmapTrackSnippetRows(userId, decksSnapshot, progressRows, srs) {
  const progressMap = new Map();
  for (const row of progressRows) {
    if (row.deckId && row.cardId) {
      progressMap.set(`${row.deckId}_${row.cardId}`, row);
    }
  }

  const roadmapDeckDocs = [];
  for (const deckDoc of decksSnapshot.docs) {
    const deckData = deckDoc.data();
    const isCookbook = deckData.type === 'cookbook' || !deckData.type;
    if (!isCookbook) continue;
    if (!roadmapService.isRoadmapUserCookbook({ id: deckDoc.id, ...deckData })) continue;
    roadmapDeckDocs.push(deckDoc);
  }

  const cardsByDeck = await Promise.all(
    roadmapDeckDocs.map(async (deckDoc) => {
      const snap = await getDocs(collection(db, `users/${userId}/decks/${deckDoc.id}/cards`));
      return { deckDoc, cardsSnap: snap };
    })
  );

  const rows = [];
  for (const { deckDoc, cardsSnap } of cardsByDeck) {
    const deckData = deckDoc.data();
    for (const cardDoc of cardsSnap.docs) {
      const cardData = cardDoc.data();
      if (!cardData.solution || !String(cardData.solution).trim()) continue;

      const p = resolveProgressForDeckCard(
        deckDoc.id,
        { id: cardDoc.id, ...cardData },
        progressMap,
        progressRows
      );
      if (!p) continue;

      const totalReviews = p.totalReviews || 0;
      const successfulReviews = p.successfulReviews || 0;
      const successRate =
        totalReviews > 0 ? Math.round((successfulReviews / totalReviews) * 100) : 0;
      const mastered = isCardMastered(p);
      const masteryVal = p.mastery ?? 0;

      // Include scheduling fields from progress so isSrsProgressRowDue matches Study Mode
      // (dueOnly filter). Omitting interval/cardPhase/lastReviewed mis-classifies review cards
      // as learning and can list them as due when Study correctly finds none.
      rows.push({
        deckId: deckDoc.id,
        deckName: deckData.name || 'Cookbook',
        language: deckData.language || '',
        roadmapTrackId: getRoadmapTrackIdFromUserDeckData(deckData),
        roadmapNodeTitle: deckData.roadmapNodeTitle || '',
        deckTags: Array.isArray(deckData.tags) ? deckData.tags : [],
        cardId: cardDoc.id,
        cardTitle: cardData.title || 'Snippet',
        successRate,
        mastered,
        nextReview: p.nextReview || null,
        interval: p.interval,
        intervalMinutes: p.intervalMinutes ?? null,
        cardPhase: p.cardPhase,
        learningStep: p.learningStep,
        repetitions: p.repetitions,
        lastReviewed: p.lastReviewed || null,
        totalReviews,
        mastery: masteryVal,
        masteryLevel: srs.getMasteryLevel(masteryVal)
      });
    }
  }

  rows.sort((a, b) => {
    if (a.mastered !== b.mastered) return a.mastered ? 1 : -1;
    if (a.successRate !== b.successRate) return a.successRate - b.successRate;
    return (a.deckName || '').localeCompare(b.deckName || '');
  });
  return rows;
}

/**
 * Anki-style queue buckets from card progress rows (due-today breakdown by phase).
 *
 * @param {Array<Record<string, unknown>>} progressRows
 * @param {Date} [now]
 * @returns {{ new: number, learning: number, review: number, dueToday: number }}
 */
export function buildQueueBucketStatsFromProgress(progressRows, now = new Date()) {
  let newDue = 0;
  let learningDue = 0;
  let reviewDue = 0;
  let dueToday = 0;

  for (const row of progressRows || []) {
    const due = isSrsProgressRowDue(row, now);
    if (!due) continue;

    dueToday += 1;
    const phase = resolveCardPhase(row);
    const totalReviews = Number(row.totalReviews) || 0;

    if (totalReviews === 0 || phase === CARD_PHASE.NEW) {
      newDue += 1;
    } else if (phase === CARD_PHASE.LEARNING || phase === CARD_PHASE.RELEARNING) {
      learningDue += 1;
    } else if (phase === CARD_PHASE.REVIEW) {
      reviewDue += 1;
    }
  }

  return {
    new: newDue,
    learning: learningDue,
    review: reviewDue,
    dueToday
  };
}

/**
 * Unified due snippet count for header badges — matches Home SRSDashboard rules:
 * library non-roadmap cookbooks + accessible roadmap track snippets with studyable cards.
 *
 * @param {string} userId
 * @param {*} srs - SpacedRepetition instance
 * @param {boolean} isPremiumUser
 */
export async function computeUnifiedDueSnippetCount(userId, srs, isPremiumUser) {
  const [decksSnap, progressRows] = await fetchUserReviewSnapshot(userId, srs);

  const deckIsRoadmap = new Map();
  const decksList = [];
  decksSnap.forEach((deckDoc) => {
    const data = deckDoc.data();
    decksList.push({ id: deckDoc.id, ...data });
    deckIsRoadmap.set(deckDoc.id, roadmapService.isRoadmapUserCookbook({ id: deckDoc.id, ...data }));
  });

  const now = new Date();
  const libraryDueDecks = await buildStudyableLibraryDueDecks(
    userId,
    decksList,
    progressRows,
    srs,
    deckIsRoadmap
  );
  const libraryDueCardCount = libraryDueDecks.reduce(
    (sum, deck) => sum + (deck.dueCards || 0),
    0
  );

  const trackRows = await fetchRoadmapTrackSnippetRows(userId, decksSnap, progressRows, srs);
  const trackDueCardCount = computeTrackDueCardCount(trackRows, isPremiumUser, now);

  return libraryDueCardCount + trackDueCardCount;
}

/**
 * Due badge refresh after study — re-reads progress only, not decks or card subcollections.
 */
export async function computeUnifiedDueSnippetCountAfterStudy(userId, srs, isPremiumUser) {
  const [decksSnap, progressRows] = await refreshUserReviewProgressRows(userId, srs);

  const deckIsRoadmap = new Map();
  const decksList = [];
  decksSnap.forEach((deckDoc) => {
    const data = deckDoc.data();
    decksList.push({ id: deckDoc.id, ...data });
    deckIsRoadmap.set(deckDoc.id, roadmapService.isRoadmapUserCookbook({ id: deckDoc.id, ...data }));
  });

  const now = new Date();
  const libraryDueDecks = await buildStudyableLibraryDueDecks(
    userId,
    decksList,
    progressRows,
    srs,
    deckIsRoadmap
  );
  const libraryDueCardCount = libraryDueDecks.reduce(
    (sum, deck) => sum + (deck.dueCards || 0),
    0
  );

  const trackRows = await fetchRoadmapTrackSnippetRows(userId, decksSnap, progressRows, srs);
  const trackDueCardCount = computeTrackDueCardCount(trackRows, isPremiumUser, now);

  return libraryDueCardCount + trackDueCardCount;
}
